/**
 * The settings registry: one declaration per knob Proxmon has.
 *
 * This file is the single source of truth for three things that used to drift
 * apart — what `loadConfig` accepts from the environment, what the database
 * stores, and what the settings dialog draws. A new setting is added here and
 * the server validates it, the importer migrates it out of `.env`, and the UI
 * grows a field for it without anybody editing a form.
 *
 * It lives in `shared` because the browser genuinely needs it: rendering a
 * number field with the right bounds, or an enum with the right options, means
 * the UI has to know the same rules the server enforces. The server still
 * validates everything again on the way in — this is for the operator's
 * benefit, not the server's trust.
 *
 * What is deliberately *not* here: `PROXMON_DATA_DIR`, `PROXMON_ENV_FILE`,
 * `PROXMON_WEB_ROOT` and `PROXMON_DEMO`. Those decide where the database is,
 * which file to read, where the SPA lives, and whether we talk to Proxmox at
 * all — questions that have to be answered before there is a database to ask.
 * They stay in the environment, and that is the whole of what `.env` is still
 * for.
 */

import {
  DEFAULT_APP_NAME,
  DEFAULT_BACKUP_WINDOW_HOURS,
  DEFAULT_EDGE_BRIDGES,
  DEFAULT_SNAPSHOT_STALE_DAYS,
} from './constants.js';

/**
 * How a value is stored, validated and drawn.
 *
 * `secret` is a string that never travels back to the browser. The dialog is
 * told whether one is set, never what it is, which is why it is its own kind
 * rather than a flag on `string`.
 */
export type SettingKind = 'string' | 'secret' | 'number' | 'boolean' | 'enum' | 'list';

export type SettingValue = string | number | boolean | string[] | null;

export type SettingSectionId =
  | 'general'
  | 'map'
  | 'polling'
  | 'snapshots'
  | 'backups'
  | 'alerts'
  | 'directory'
  | 'server'
  | 'logging';

export interface SettingSection {
  id: SettingSectionId;
  title: string;
  blurb: string;
}

export const SETTING_SECTIONS: readonly SettingSection[] = [
  { id: 'general', title: 'General', blurb: 'What this install calls itself, and where it lives.' },
  { id: 'map', title: 'Map', blurb: 'What the map treats as notable.' },
  { id: 'polling', title: 'Polling', blurb: 'How often Proxmox is asked, and how much is asked for.' },
  { id: 'snapshots', title: 'Snapshots', blurb: 'Flagging guests that are carrying a snapshot.' },
  { id: 'backups', title: 'Backups', blurb: 'How far back the backup timeline reads.' },
  { id: 'alerts', title: 'Alerts', blurb: 'Posting to a Teams channel when something is wrong.' },
  { id: 'directory', title: 'Directory', blurb: 'Signing in with a domain account instead of the local one.' },
  { id: 'server', title: 'Server', blurb: 'How the HTTP listener behaves. Most of this needs a restart.' },
  { id: 'logging', title: 'Logging', blurb: 'How much Proxmon says, and in what shape.' },
] as const;

export interface SettingDef {
  key: string;
  section: SettingSectionId;
  label: string;
  /** one or two sentences, shown under the field */
  help: string;
  kind: SettingKind;
  default: SettingValue;
  /**
   * Environment variables this setting was read from, highest precedence
   * first. Used once, to import an existing `.env` into the database, and
   * afterwards only to warn that an env var is now being ignored.
   */
  env: readonly string[];
  /** numbers only; the server clamps rather than rejects, as it always has */
  min?: number;
  max?: number;
  /** a short suffix drawn inside the field, e.g. "ms", "days", "%" */
  unit?: string;
  /** enums only */
  options?: readonly string[];
  /** stored and used, but the running process will not pick it up until restart */
  restart?: boolean;
  placeholder?: string;
  /**
   * Hide the field unless this other setting is truthy. Purely cosmetic — a
   * directory timeout is noise until there is a directory host.
   */
  showWhen?: string;
}

/**
 * Every setting, in the order the dialog draws them.
 *
 * The defaults here are the defaults the app has always had; `loadConfig` used
 * to carry them inline and now reads them from this table, so there is exactly
 * one number to change when one of them turns out to be wrong.
 */
export const SETTING_DEFS: readonly SettingDef[] = [
  // --- general ------------------------------------------------------------
  {
    key: 'appName',
    section: 'general',
    label: 'Display name',
    help: 'Shown in the title bar and on every alert card. Useful when you run one Proxmon per site.',
    kind: 'string',
    default: DEFAULT_APP_NAME,
    env: ['PROXMON_APP_NAME'],
    placeholder: DEFAULT_APP_NAME,
  },
  {
    key: 'publicUrl',
    section: 'general',
    label: 'Public URL',
    help: 'Where people reach this Proxmon from outside. Only used for the "Open the map" button on alert cards; leave it empty and the button is left off.',
    kind: 'string',
    default: '',
    env: ['PROXMON_PUBLIC_URL'],
    placeholder: 'https://proxmon.example.com',
  },

  // --- map ----------------------------------------------------------------
  {
    key: 'edgeBridges',
    section: 'map',
    label: 'Internet-facing bridges',
    help: 'Guests on these bridges are drawn with an exposure halo. Comma-separated, normally just vmbr0.',
    kind: 'list',
    default: DEFAULT_EDGE_BRIDGES,
    env: ['PROXMON_EDGE_BRIDGES'],
    placeholder: 'vmbr0',
  },

  // --- polling ------------------------------------------------------------
  {
    key: 'pollIntervalMs',
    section: 'polling',
    label: 'Status interval',
    help: 'The fast loop: node and guest status, CPU, memory, network counters. Every cluster member answers this one, so going below a couple of seconds buys very little.',
    kind: 'number',
    default: 5000,
    env: ['PROXMON_POLL_INTERVAL_MS'],
    min: 1000,
    max: 600000,
    unit: 'ms',
  },
  {
    key: 'detailIntervalMs',
    section: 'polling',
    label: 'Detail interval',
    help: 'The slow loop: guest configs, bridges, ZFS pools, storages, task logs. This is the expensive one, and almost none of it changes minute to minute.',
    kind: 'number',
    default: 30000,
    env: ['PROXMON_DETAIL_INTERVAL_MS'],
    min: 5000,
    max: 3600000,
    unit: 'ms',
  },
  {
    key: 'endpointCooldownMs',
    section: 'polling',
    label: 'Endpoint cooldown',
    help: 'How long a PVE endpoint stays benched after it fails, before the pool will try it again.',
    kind: 'number',
    default: 20000,
    env: ['PROXMON_ENDPOINT_COOLDOWN_MS'],
    min: 1000,
    max: 600000,
    unit: 'ms',
  },
  {
    key: 'qemuAgentIps',
    section: 'polling',
    label: 'Ask the QEMU guest agent for IPs',
    help: 'Gets a VM’s real addresses when the agent is installed and running. Without it, a VM shows only what its config declares.',
    kind: 'boolean',
    default: true,
    env: ['PROXMON_QEMU_AGENT_IPS'],
  },
  {
    key: 'qemuAgentBudget',
    section: 'polling',
    label: 'Agents per cycle',
    help: 'How many guest agents to interrogate in one detail cycle. A guest without an agent costs a failed call, so this caps what a mostly agentless estate wastes.',
    kind: 'number',
    default: 12,
    env: ['PROXMON_QEMU_AGENT_BUDGET'],
    min: 0,
    max: 500,
    showWhen: 'qemuAgentIps',
  },

  // --- snapshots ----------------------------------------------------------
  {
    key: 'snapshots',
    section: 'snapshots',
    label: 'List guest snapshots',
    help: 'Lets the map flag guests carrying a snapshot. Costs one extra API call per guest per detail cycle.',
    kind: 'boolean',
    default: true,
    env: ['PROXMON_SNAPSHOTS'],
  },
  {
    key: 'snapshotStaleDays',
    section: 'snapshots',
    label: 'Stale after',
    help: 'How old the oldest snapshot on a guest has to be before the map turns its badge red. The legend states this number, so it is never a surprise.',
    kind: 'number',
    default: DEFAULT_SNAPSHOT_STALE_DAYS,
    env: ['PROXMON_SNAPSHOT_STALE_DAYS'],
    min: 1,
    max: 3650,
    unit: 'days',
    showWhen: 'snapshots',
  },

  // --- backups ------------------------------------------------------------
  {
    key: 'backupWindowHours',
    section: 'backups',
    label: 'Timeline window',
    help: 'How far back the backup timeline reaches. Zero stops Proxmon reading node task logs at all. Capped at a fortnight, past which the read stops being cheap and the timeline stops being readable.',
    kind: 'number',
    default: DEFAULT_BACKUP_WINDOW_HOURS,
    env: ['PROXMON_BACKUP_WINDOW_HOURS'],
    min: 0,
    max: 336,
    unit: 'hours',
  },

  // --- alerts -------------------------------------------------------------
  {
    key: 'alertsEnabled',
    section: 'alerts',
    label: 'Post alerts to Teams',
    help: 'Nothing is posted until a webhook is set as well. Switch this off to mute the channel without losing the webhook.',
    kind: 'boolean',
    default: true,
    env: [],
  },
  {
    key: 'teamsWebhook',
    section: 'alerts',
    label: 'Teams webhook',
    help: 'In Teams: channel … > Workflows > "Post to a channel when a webhook request is received", then paste the URL it hands you. That URL carries its own signature, so it is a password: Proxmon stores it, never logs it, and never sends it back to this page.',
    kind: 'secret',
    default: '',
    env: ['PROXMON_TEAMS_WEBHOOK', 'TEAMS_WEBHOOK'],
    placeholder: 'https://prod-00.westeurope.logic.azure.com:443/workflows/…',
  },
  {
    key: 'zfsUsedPercent',
    section: 'alerts',
    label: 'ZFS fill threshold',
    help: 'A pool at or above this percent full is reported. Below 50 would be an alert about a healthy pool; above 100 would never fire.',
    kind: 'number',
    default: 90,
    env: ['PROXMON_ZFS_ALERT_PERCENT', 'ZFS_FREE_ALERT_PERCENTS'],
    min: 50,
    max: 100,
    unit: '%',
  },
  {
    key: 'alertConfirmPolls',
    section: 'alerts',
    label: 'Confirm after',
    help: 'Consecutive polls a problem must persist before it is announced. At the default status interval, three polls is a fifteen-second grace period — long enough to ride out a live migration, which PVE reports as a stopped guest.',
    kind: 'number',
    default: 3,
    env: ['PROXMON_ALERT_CONFIRM_POLLS'],
    min: 1,
    max: 100,
    unit: 'polls',
  },
  {
    key: 'alertClearPolls',
    section: 'alerts',
    label: 'Clear after',
    help: 'Consecutive polls a problem must be gone before it is called recovered.',
    kind: 'number',
    default: 2,
    env: ['PROXMON_ALERT_CLEAR_POLLS'],
    min: 1,
    max: 100,
    unit: 'polls',
  },
  {
    key: 'alertReminderMinutes',
    section: 'alerts',
    label: 'Remind every',
    help: 'Re-post standing problems this often, so a long outage does not scroll out of the channel. Zero disables the nudge.',
    kind: 'number',
    default: 0,
    env: ['PROXMON_ALERT_REMINDER_MINUTES'],
    min: 0,
    max: 1440,
    unit: 'min',
  },
  {
    key: 'alertTimeoutMs',
    section: 'alerts',
    label: 'Delivery timeout',
    help: 'How long to wait for the webhook before treating a send as failed. Failed sends are retried twice.',
    kind: 'number',
    default: 8000,
    env: ['PROXMON_ALERT_TIMEOUT_MS'],
    min: 1000,
    max: 60000,
    unit: 'ms',
  },

  // --- directory ----------------------------------------------------------
  {
    key: 'ldapHost',
    section: 'directory',
    label: 'Server',
    help: 'Hostname of a domain controller. Directory sign-in switches on once this and a base DN are both set.',
    kind: 'string',
    default: '',
    env: ['PROXMON_LDAP_HOST'],
    placeholder: 'dc01.example.local',
  },
  {
    key: 'ldapBaseDn',
    section: 'directory',
    label: 'Base DN',
    help: 'Where the user search starts.',
    kind: 'string',
    default: '',
    env: ['PROXMON_LDAP_BASE_DN'],
    placeholder: 'DC=example,DC=local',
  },
  {
    key: 'ldapPort',
    section: 'directory',
    label: 'Port',
    help: '389 for plain or StartTLS, 636 for LDAPS.',
    kind: 'number',
    default: 389,
    env: ['PROXMON_LDAP_PORT'],
    min: 1,
    max: 65535,
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapUseStartTls',
    section: 'directory',
    label: 'StartTLS',
    help: 'Upgrade the connection to TLS on port 389. Without this or LDAPS, domain passwords cross the network in clear.',
    kind: 'boolean',
    default: false,
    env: ['PROXMON_LDAP_USE_STARTTLS'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapUseSsl',
    section: 'directory',
    label: 'LDAPS',
    help: 'Connect over TLS from the start, normally on port 636.',
    kind: 'boolean',
    default: false,
    env: ['PROXMON_LDAP_USE_SSL'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapTlsVerify',
    section: 'directory',
    label: 'Verify the certificate',
    help: 'Leave this on. If the controller uses an internal CA, point the CA file below at it rather than turning verification off.',
    kind: 'boolean',
    default: true,
    env: ['PROXMON_LDAP_TLS_VERIFY'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapCaCertFile',
    section: 'directory',
    label: 'CA certificate file',
    help: 'Path on this server to the PEM of the internal CA that signed the controller certificate.',
    kind: 'string',
    default: '',
    env: ['PROXMON_LDAP_CA_CERT_FILE'],
    placeholder: '/etc/ssl/certs/internal-ca.pem',
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapBindDn',
    section: 'directory',
    label: 'Service account DN',
    help: 'A read-only account used to find the person signing in. It never needs write rights anywhere.',
    kind: 'string',
    default: '',
    env: ['PROXMON_LDAP_BIND_DN'],
    placeholder: 'CN=proxmon,OU=Services,DC=example,DC=local',
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapBindPassword',
    section: 'directory',
    label: 'Service account password',
    help: 'Stored here, never sent back to this page.',
    kind: 'secret',
    default: '',
    env: ['PROXMON_LDAP_BIND_PASSWORD'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapLoginAttr',
    section: 'directory',
    label: 'Login attribute',
    help: 'sAMAccountName on Active Directory, uid on OpenLDAP.',
    kind: 'string',
    default: 'sAMAccountName',
    env: ['PROXMON_LDAP_LOGIN_ATTR'],
    placeholder: 'sAMAccountName',
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapUserFilter',
    section: 'directory',
    label: 'User filter',
    help: 'Extra LDAP filter ANDed onto the lookup, to narrow which accounts are even considered. Empty accepts any user object.',
    kind: 'string',
    default: '',
    env: ['PROXMON_LDAP_USER_FILTER'],
    placeholder: '(memberOf=CN=Ops,DC=example,DC=local)',
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapNameAttr',
    section: 'directory',
    label: 'Display-name attribute',
    help: 'Which attribute to show as the person’s name.',
    kind: 'string',
    default: 'displayName',
    env: ['PROXMON_LDAP_NAME_ATTR'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapEmailAttr',
    section: 'directory',
    label: 'Email attribute',
    help: 'Which attribute holds the address.',
    kind: 'string',
    default: 'mail',
    env: ['PROXMON_LDAP_EMAIL_ATTR'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapTimeoutMs',
    section: 'directory',
    label: 'Timeout',
    help: 'Kept short on purpose: an unreachable directory must not leave someone staring at the sign-in form before the local account is offered.',
    kind: 'number',
    default: 5000,
    env: ['PROXMON_LDAP_TIMEOUT_MS'],
    min: 1000,
    max: 60000,
    unit: 'ms',
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapAutoCreateUsers',
    section: 'directory',
    label: 'Admit any directory account',
    help: 'Off, only accounts on the Users list may sign in. On, anyone the directory accepts is let in with the default role below — which on a domain of any size is a much larger group than you think.',
    kind: 'boolean',
    default: false,
    env: ['PROXMON_LDAP_AUTO_CREATE_USERS'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapDefaultRole',
    section: 'directory',
    label: 'Default role',
    help: 'What a newly admitted directory account gets.',
    kind: 'enum',
    default: 'viewer',
    env: ['PROXMON_LDAP_DEFAULT_ROLE'],
    options: ['viewer', 'admin'],
    showWhen: 'ldapHost',
  },
  {
    key: 'ldapBootstrapAdmins',
    section: 'directory',
    label: 'Pinned admins',
    help: 'These usernames are made admin at every sign-in, whatever the Users list says. This is the way back in from an empty user table, so keep at least one here.',
    kind: 'list',
    default: [],
    env: ['PROXMON_LDAP_BOOTSTRAP_ADMINS'],
    placeholder: 'sulyvan, matthieu',
    showWhen: 'ldapHost',
  },

  // --- server -------------------------------------------------------------
  {
    key: 'host',
    section: 'server',
    label: 'Bind address',
    help: '0.0.0.0 listens everywhere. Set 127.0.0.1 when a reverse proxy on this machine is the only thing that should reach it.',
    kind: 'string',
    default: '0.0.0.0',
    env: ['PROXMON_HOST'],
    restart: true,
    placeholder: '0.0.0.0',
  },
  {
    key: 'port',
    section: 'server',
    label: 'Port',
    help: 'The port the HTTP listener binds.',
    kind: 'number',
    default: 8420,
    env: ['PROXMON_PORT'],
    min: 1,
    max: 65535,
    restart: true,
  },
  {
    key: 'trustProxy',
    section: 'server',
    label: 'Behind a reverse proxy',
    help: 'Read the client address from X-Forwarded-For. Without it every request behind a proxy looks like one client, which makes rate limiting useless. Never switch it on when the port is reachable directly — the header is then whatever the caller says it is.',
    kind: 'boolean',
    default: false,
    env: ['PROXMON_TRUST_PROXY'],
    restart: true,
  },
  {
    key: 'cookieSecure',
    section: 'server',
    label: 'HTTPS-only cookies',
    help: 'Marks the session cookie Secure and sends HSTS. Switch it on once TLS terminates in front of this, and not before: a Secure cookie over plain HTTP is a cookie the browser throws away.',
    kind: 'boolean',
    default: false,
    env: ['PROXMON_COOKIE_SECURE', 'PROXMON_BEHIND_TLS'],
    restart: true,
  },
  {
    key: 'sessionTtlHours',
    section: 'server',
    label: 'Session lifetime',
    help: 'Sessions slide: every request pushes the expiry out, so an admin with the map open is not signed out mid-incident. This is how long an idle one survives.',
    kind: 'number',
    default: 12,
    env: ['PROXMON_SESSION_TTL_HOURS'],
    min: 1,
    max: 8760,
    unit: 'hours',
  },

  // --- logging ------------------------------------------------------------
  {
    key: 'logLevel',
    section: 'logging',
    label: 'Level',
    help: 'debug is loud: every poll, every endpoint decision, every skipped agent.',
    kind: 'enum',
    default: 'info',
    env: ['PROXMON_LOG_LEVEL'],
    options: ['debug', 'info', 'warn', 'error'],
  },
  {
    key: 'logJson',
    section: 'logging',
    label: 'JSON lines',
    help: 'One JSON object per line instead of human-readable text. What you want when something is shipping these to a log store.',
    kind: 'boolean',
    default: false,
    env: ['PROXMON_LOG_JSON'],
    restart: true,
  },
  {
    key: 'logColor',
    section: 'logging',
    label: 'Colour',
    help: 'ANSI colour in the human-readable format. Defaults to on when stdout is a terminal.',
    kind: 'boolean',
    default: false,
    env: ['PROXMON_LOG_COLOR'],
    restart: true,
  },
] as const;

export const SETTING_BY_KEY: ReadonlyMap<string, SettingDef> = new Map(SETTING_DEFS.map((d) => [d.key, d]));

/** Every setting's key, for the record types below. */
export type SettingKey = (typeof SETTING_DEFS)[number]['key'];

/** The full settings record as the server resolves it, secrets included. */
export type SettingsValues = Record<string, SettingValue>;

export function defaultSettingValues(): SettingsValues {
  const out: SettingsValues = {};
  for (const def of SETTING_DEFS) {
    out[def.key] = Array.isArray(def.default) ? [...def.default] : def.default;
  }
  return out;
}

/**
 * Coerce one stored or typed value into the shape its definition promises.
 *
 * Returns `undefined` for input that cannot be read as the right type at all,
 * which the caller treats as "leave it alone" rather than "set it to the
 * default" — a malformed patch must never silently reset a working setting.
 *
 * Numbers clamp rather than reject. That is how `loadConfig` has always
 * treated out-of-range environment values, and an operator who types 999 into
 * a percentage is better served by 100 than by a red box.
 */
export function coerceSetting(def: SettingDef, raw: unknown): SettingValue | undefined {
  switch (def.kind) {
    case 'boolean': {
      if (typeof raw === 'boolean') return raw;
      if (typeof raw === 'string') {
        const text = raw.trim().toLowerCase();
        if (['1', 'true', 'yes', 'on'].includes(text)) return true;
        if (['0', 'false', 'no', 'off', ''].includes(text)) return false;
      }
      return undefined;
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
      if (!Number.isFinite(n)) return undefined;
      const lo = def.min ?? Number.NEGATIVE_INFINITY;
      const hi = def.max ?? Number.POSITIVE_INFINITY;
      return Math.min(hi, Math.max(lo, Math.round(n)));
    }
    case 'enum': {
      const text = String(raw ?? '').trim();
      return def.options?.includes(text) ? text : undefined;
    }
    case 'list': {
      const parts = Array.isArray(raw) ? raw.map((v) => String(v)) : String(raw ?? '').split(',');
      const cleaned = parts.map((s) => s.trim()).filter(Boolean);
      // De-duplicated case-insensitively: "vmbr0, VMBR0" is one bridge, and
      // two spellings of one pinned admin is one admin.
      const seen = new Set<string>();
      const out: string[] = [];
      for (const part of cleaned) {
        const key = part.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(part);
      }
      return out;
    }
    case 'secret':
    case 'string':
      if (raw === null) return '';
      if (typeof raw === 'object') return undefined;
      return String(raw).trim();
    default:
      return undefined;
  }
}

/** True when a setting holds nothing — used for `showWhen` and "is it set?". */
export function isBlank(value: SettingValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'boolean') return !value;
  return false;
}

// --- PVE endpoints --------------------------------------------------------

/**
 * A configured Proxmox API, as the settings dialog sees it.
 *
 * The secret half — token secret, or ticket password — is never part of this
 * shape. `hasSecret` is all the UI is told, which is all it needs to draw
 * "configured" beside a replace field.
 */
export interface PveEndpointView {
  id: string;
  label: string;
  baseUrl: string;
  authKind: 'token' | 'ticket';
  /** token id, or the ticket username; not itself a secret */
  authUser: string;
  hasSecret: boolean;
  priority: number;
  caPath: string | null;
  fingerprint: string | null;
  insecureTls: boolean;
  timeoutMs: number;
}

/**
 * Create or update an endpoint. Omitting the secret on an update keeps the
 * stored one, so an operator can fix a typo in a label without re-pasting a
 * token they may no longer have.
 */
export interface PveEndpointInput {
  id: string;
  label?: string;
  baseUrl: string;
  authKind: 'token' | 'ticket';
  authUser: string;
  secret?: string | null;
  priority?: number;
  caPath?: string | null;
  fingerprint?: string | null;
  insecureTls?: boolean;
  timeoutMs?: number;
}

// --- the settings API -----------------------------------------------------

/** What `GET /api/settings` answers. */
export interface SettingsResponse {
  /** every non-secret value; a secret's slot holds null */
  values: SettingsValues;
  /** keys of the secrets that currently hold something */
  secretsSet: string[];
  endpoints: PveEndpointView[];
  /** true when settings were imported out of the environment on this boot */
  imported: boolean;
  /** changed since the process started, and waiting for a restart to apply */
  pendingRestart: string[];
}

export type SettingsPatch = Record<string, unknown>;
